// ink-component
import React, { HTMLAttributes, MouseEvent, ReactNode } from 'react';

import { ThemeProvider } from 'styled-components';
import { TagElement, TagBody, TagText } from './styles';

import TagButton from './TagButton/TagButton';
import TagContainer from './TagContainer/TagContainer';
import { FeedbackVariants } from '../_common/Types/GlobalTypes';

import { CSSWidthType } from '../../helpers/constants/cssPropTypes';

import getGlobalProps from '../../helpers/constants/global-props';
import Icon from '../Icon/Icon';

const globalProps = getGlobalProps({});

type ActionType = {
  size?: 'mini' | 'default';
  active?: boolean;
};

type FlexVariants =
  | 'flex-orange-light'
  | 'flex-orange-medium'
  | 'flex-orange-dark'
  | 'flex-red-light'
  | 'flex-red-medium'
  | 'flex-red-dark'
  | 'flex-green-light'
  | 'flex-green-medium'
  | 'flex-green-dark'
  | 'flex-lime-light'
  | 'flex-lime-medium'
  | 'flex-lime-dark'
  | 'flex-yellow-light'
  | 'flex-yellow-medium'
  | 'flex-yellow-dark'
  | 'flex-blue-light'
  | 'flex-blue-medium'
  | 'flex-blue-dark'
  | 'flex-gray-light'
  | 'flex-gray-medium'
  | 'flex-gray-dark'
  | 'flex-purple-light'
  | 'flex-purple-medium'
  | 'flex-purple-dark'
  | 'flex-turquoise-light'
  | 'flex-turquoise-medium'
  | 'flex-turquoise-dark';

export interface TagProps extends HTMLAttributes<HTMLDivElement> {
  /** Tag content */
  children?: ReactNode;
  /** onClick callback */
  onClick?: (event: MouseEvent) => void;
  /** A render prop that adds an interactive area at the end of the tag. Used with a Tag.Button inside. */
  action?: (obj: ActionType) => void;
  /** Active state, only applies to default variant */
  active?: boolean;
  /** Draggable state (visual only) */
  draggable?: boolean;
  /** Dragging state (visual only) */
  dragging?: boolean;
  /** Set a css width value to ellipsis text content */
  maxWidth?: CSSWidthType;
  /** The size of the tag */
  size?: 'mini' | 'default';
  /** Variant */
  variant:
    | FlexVariants
    | Extract<
        FeedbackVariants,
        | 'feedback-positive'
        | 'feedback-negative'
        | 'feedback-notice'
        | 'feedback-pending'
        | 'feedback-informational'
        | 'feedback-neutral'
      >;

  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

/**
 * @purpose A tag visual for categorizing data
 * @group Tag
 * @categories accessory
 * @HTMLType HTMLDivElement
 */

function Tag(props: TagProps) {
  const {
    action,
    active,
    children,
    className,
    draggable,
    dragging,
    maxWidth,
    onClick,
    size,
    style,
    variant,
    ...otherProps
  } = props;

  const bodyProps = {
    onClick,
    tabIndex: onClick ? 0 : -1,
    // undefined is not ideal but we haven't found a better option yet
    role: onClick ? 'button' : undefined,
  };

  const notFlexVariant = !variant.startsWith('flex');

  return (
    <ThemeProvider
      theme={currentTheme => ({
        ...currentTheme,
        tagActive: active,
        tagClickable: !!onClick,
        tagDraggable: notFlexVariant && draggable,
        tagDragging: notFlexVariant && dragging,
        tagSize: size,
        tagVariant: variant,
        tagEllipsis: maxWidth,
      })}
    >
      <TagElement {...otherProps} {...globalProps}>
        <TagBody {...bodyProps}>
          {notFlexVariant && draggable && <Icon type="drag-n-drop" size="md" meaning="draggable tag" />}
          <TagText>{children}</TagText>
        </TagBody>
        {notFlexVariant && action && action({ size, active })}
      </TagElement>
    </ThemeProvider>
  );
}

Tag.defaultProps = {
  active: false,
  dragging: false,
  size: 'default',
  variant: 'feedback-informational',
};

Tag.Button = TagButton;
Tag.Container = TagContainer;

export { TagButtonProps } from './TagButton/TagButton';
export { TagContainerProps } from './TagContainer/TagContainer';

Tag.displayName = 'Tag';

export default Tag;
